<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">eTA Visa Requests</h3>
        <div class="text-xs-right">
          <sidebar-filter-button class="mr-2">
            <div class="px-2">
              <v-autocomplete
                  class="mb-4"
                  :items="offices"
                  @change="fetchFiltersData"
                  clearable
                  flat
                  hide-details
                  item-text="name"
                  item-value="id"
                  label="Office"
                  prepend-inner-icon="filter_list"
                  solo
                  outlined
                  v-model="filters.office"
              />
              <v-autocomplete
                  class="mb-4"
                  :items="countries"
                  @change="fetchFiltersData"
                  flat
                  hide-details
                  label="Countries"
                  prepend-inner-icon="filter_list"
                  solo
                  clearable
                  outlined
                  v-model="filters.country"
              />
              <div class="mb-4">
                <custom-date-time-range-picker
                    @change="fetchFiltersData"
                    @input="fetchFiltersData"
                    :outlined="true"
                    v-model="filters.date_time_range"
                />
              </div>

              <div class="mb-4">
                <custom-date-range-picker
                    v-model="filters.date_range"
                    @input="fetchFiltersData"
                    :outlined="true"
                    @change="fetchFiltersData"
                />
              </div>
            </div>
          </sidebar-filter-button>
          <export-excel-button
              :export-data="exportData"
              v-if="$oauth.checkIfHasPermission(['export eta-visa-request'])"
          />
          <refresh-button :click-function="refreshData"></refresh-button>
        </div>
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-layout class="py-1 mx-2" row wrap>
            <v-flex class="xs3">
              <v-text-field
                  flat
                  solo
                  prepend-inner-icon="search"
                  hide-details
                  :placeholder="this.filters.status === 'approved'?'Search using visa number':'Search using submission no'"
                  v-model="filters.query"
                  @keypress.enter="fetchFiltersData"
                  class="hidden-sm-and-down"
              />
            </v-flex>

            <v-flex class="xs3">
              <custom-date-time-range-picker
                  v-model="filters.date_range"
                  @change="fetchFiltersData"/>
            </v-flex>
            <v-flex class="xs2">
              <v-autocomplete
                  :items="status_options"
                  @change="fetchFiltersData"
                  flat
                  hide-details
                  label="Status"
                  prepend-inner-icon="filter_list"
                  solo
                  v-model="filters.status"
              />
            </v-flex>
          </v-layout>
          <v-divider></v-divider>

          <v-card-text class="pa-0">
            <v-data-table
                :footer-props="footerProps"
                :headers="headers"
                :items="data"
                :loading="loading"
                :options.sync="pagination"
                class="elevation-0"
                hide-default-footer
                v-if="$oauth.checkIfHasPermission(['read eta-visa-request'])"
            >

              <template v-slot:item.passport_number="{ item }">
                <span class="font-weight-bold" style="font-size: 12px;">{{ item.full_name }}</span>
                <br/>
                <span class style="font-size: 12px;">{{ item.passport_number }}</span>
              </template>

              <template v-slot:item.office_name="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ item.office_code.toUpperCase() }}</span>
                  </template>
                  <span>{{ item.office_name }}</span>
                </v-tooltip>
              </template>

              <template v-slot:item.country_name="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on" v-if="item.country_code">{{ item.country_code }}</span>
                    <span v-on="on" v-else>{{ item.country_name }}</span>
                  </template>
                  <span>{{ item.country_name }}</span>
                </v-tooltip>
              </template>
              <template v-slot:item.condition="{ item }">
                <span class="font-weight-bold" style="font-size: 12px;">{{ item.visa_type }}</span>
                <br/>
                <span class style="font-size: 12px;">{{ item.condition }}</span>
              </template>

              <template v-slot:item.channel="{ item }">
                <span class="font-weight-bold" style="font-size: 12px;">
                  {{ item.channel === 1 ? 'Android' : item.channel === 2 ? 'IOS' : 'Web' }}
                </span>
                <br/>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip :color="mapStatusNameColor(item.status)" text-color="white">
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.action="{ item }">
                <td class="pl-0">
                  <v-tooltip top v-if="item.voucher_number && item.statusNumber === 48">
                    <template v-slot:activator="{ on }">
                      <v-btn
                          @click="checkPayment(item)"
                          class="mr-2"
                          color="green lighten-1"
                          dark
                          dense
                          fab
                          v-on="on"
                          x-small
                      >
                        <v-icon small>credit_score</v-icon>
                      </v-btn>
                    </template>
                    <span>Check Payment</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                          :href="'/system/visa-requests/view/'+item.submission_no"
                          :to="'/system/visa-requests/view/'+item.submission_no"
                          @click="view(item)"
                          class="mr-2"
                          color="primary"
                          dark
                          dense
                          fab
                          v-on="on"
                          x-small
                      >
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>View</span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
            <issued-visa-footer
                :disable-next="disableNext"
                :disable-prev="disablePrev"
                :items-loading="totalItemsLoading"
                :perPage="pagination.itemsPerPage"
                :showing-of="showingOf"
                :total="total"
                @getCount="getCount"
                @nextClick="handleNextClick"
                @prevClick="handlePrevClick"
                @rowChanged="fetchDataWithRow"
            />
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
import {mapActions, mapGetters} from "vuex";
import CustomDateRangePicker from "../../../components/CustomDateRangePicker";
import {commonStoreMixin} from "../../../mixins/commonStoreMixin";
import IssuedVisaFooter from "../../../components/footer/IssuedVisaFooter";
import ExportExcelButton from "../../../components/ExportExcelButton";
import SidebarFilterButton from "../../../components/SidebarFilterButton";
import RefreshButton from "@/components/RefreshButton";
import {mapStatusNameColor} from "@/constants/AppConstant";
import CustomDateTimeRangePicker from "@/components/CustomDateTimeRangePicker.vue";

export default {
  data() {
    return {
      title: "Visa Request",
      pagination: {
        itemsPerPage: 25,
        sortBy: ["created_at"],
        sortDesc: ["true"]
      },
      searchQuery: "",
      visa_options: [
        {
          text: "All",
          value: "all"
        },
        {
          text: "New Visa",
          value: "new-visa"
        },
        {
          text: "Conversion",
          value: "convert"
        },
        {
          text: "Extension",
          value: "extend"
        }
      ],
      status_options: [
        {
          text: "Applied",
          value: "applied"
        },
        {
          text: "Verified",
          value: "verified"
        },
        {
          text: "Voucher Generated",
          value: "voucher_generated"
        },
        {
          text: "Paid",
          value: "paid"
        },
        {
          text: "Rejected",
          value: "rejected"
        },
        {
          text: "Approved",
          value: "approved"
        }
      ]
    };
  },
  components: {
    CustomDateTimeRangePicker,
    RefreshButton, CustomDateRangePicker, IssuedVisaFooter, ExportExcelButton, SidebarFilterButton
  },
  mixins: [commonStoreMixin],
  computed: {
    ...mapGetters({
      loading: "VisaRequestList/reportLoading",
      serverPagination: "VisaRequestList/serverPagination",
      countries: "CountryList/all"
    }),
    offices() {
      return this.$store.state.OfficeList.offices;
    },
    from_main_office() {
      return this.$store.state.AuthUser.user.from_main_office;
    },
    headers() {
      let headers = [
        {
          text: "S.N.",
          value: "s_no",
          sortable: false
        },
        {
          text: "Visa number",
          value: "visa_number",
          sortable: false
        },
        {
          text: "Mobile number",
          value: "mobile_number",
          sortable: false
        },
        {
          text: "Traveller",
          value: "passport_number",
          sortable: false
        },
        {
          text: "Country",
          value: "country_name",
        },
        {
          text: "Visa Type",
          value: "condition",
          width: 220,
          sortable: false
        },
        {
          text: "Office",
          value: "office_name",
        },

        {
          text: "Apply Type",
          value: "apply_type",
          sortable: false
        },
        {
          text: "Channel",
          value: "channel",
          sortable: false
        },
        {
          text: "Status",
          value: "status",
          sortable: false
        },
        {
          text: "Applied On",
          value: "created_at"
        },
        {
          text: "Action",
          value: "action",
          sortable: false
        }
      ];
      if (this.filters.status === "paid" || this.filters.status === "applied" || this.filters.status === 'verified') {
        headers[1] = {
          text: "Sub. ID",
          value: "submission_no",
          sortable: false
        };
      }
      // else {
      //     headers = headers.filter(item => item.value !== 'apply_type');
      // }

      return headers;
    },
    countries() {
      return this.$store.state.CountryList.all;
    }
  },
  methods: {
    mapStatusNameColor,
    ...mapActions("CommonList", ["resetState", "getAll", "create", "delete"]),
    ...mapActions("VisaRequestSingle", ["checkApplicationPaymentStatus"]),
    refreshData() {
      this.fetchData();
    },
    view(item) {
      this.$router.push({
        name: "ViewVisaRequest",
        params: {id: item.submission_no}
      });
    },
    async checkPayment(item) {
      try {
        await this.checkApplicationPaymentStatus(item.submission_no);
        this.$store.commit("Notification/showSuccess", "Amount has been paid");
      } catch (e) {
        this.$store.commit("Notification/showError", "The amount is unpaid");
      }
    },
  },
  mounted() {
    let params = this.$route.params;
    let query = this.$route.query;

    this.stateName = 'VisaRequestList';
    this.setFilters({
      type: "eta",
      status: "applied"
    });

    this.commonState = this.$store.state.VisaRequestList;

    if (this.$store.state.OfficeList.offices.length === 0) {
      this.$store.dispatch("OfficeList/fetchData");
    }
    if (this.$store.state.CountryList.all.length === 0) {
      this.$store.dispatch('CountryList/fetchCountries');
    }

    if (query.status) {
      this.filters.status = query.status
    }
    if (params.fromRoute) {
      this.filters.status = params.type
      this.filters.date_range = {"start": params.start_date, "end": params.end_date}
    }
    this.pagination = this.serverPagination;
  },
  watch: {
    '$route.query': function (val, old) {
      if (val.status) {
        this.filters.status = val.status
        this.fetchData()
      }
    },

    '$route.params': function (newVal, oldVal) {
      let params = newVal
      if (params.fromRoute) {
        let editedFilter = {};
        editedFilter.status = params.type;
        editedFilter.date_range = {"start": params.start_date, "end": params.end_date}
        this.setFilters(editedFilter)
        this.fetchData()
      }
    }
  }
};
</script>
